import type { PropsWithChildren } from 'react';
import clsx from 'clsx';
import { WorkspaceSwitcher } from 'components/WorkspaceSwitcher/WorkspaceSwitcher';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { DeviceSize, useDeviceSize } from 'wrappers/DeviceSizeProvider';
import { useWorkspace } from 'wrappers/WorkspaceProvider';

import { TertiaryButton } from '@fragment/ui/src/components/Button/TertiaryButton/TertiaryButton';
import { LoadingIndicator } from '@fragment/ui/src/components/LoadingText';

import { ToastProvider } from '../../wrappers/ToastProvider';
import { PaymentsNav } from '../SideNav/PaymentsNav';

const AmbientBlob = () => {
  const blobRef = useRef<HTMLDivElement>(null);
  const pos = useRef({ x: 0, y: 0 });
  const vel = useRef({ x: 0, y: 0 });
  const target = useRef({ x: 0, y: 0 });
  const isIdle = useRef(true);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phase = useRef(0);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      target.current = {
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      };
      isIdle.current = false;
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        isIdle.current = true;
      }, 1000);
    };

    window.addEventListener('mousemove', onMouseMove);

    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      if (isIdle.current) {
        phase.current += dt * 0.04;
        const p = phase.current;
        target.current = {
          x: Math.sin(p * 1.3) * 9 + Math.sin(p * 0.7 + 1.2) * 4,
          y: Math.cos(p * 1.1 + 0.8) * 9 + Math.cos(p * 0.5 + 2.1) * 4,
        };
      }

      const spring = 4.0;
      const damping = 3.8;
      vel.current.x +=
        ((target.current.x - pos.current.x) * spring -
          vel.current.x * damping) *
        dt;
      vel.current.y +=
        ((target.current.y - pos.current.y) * spring -
          vel.current.y * damping) *
        dt;
      pos.current.x += vel.current.x * dt;
      pos.current.y += vel.current.y * dt;

      if (blobRef.current) {
        blobRef.current.style.transform = `translate(${pos.current.x}%, ${pos.current.y}%)`;
      }

      raf.current = requestAnimationFrame(tick);
    };

    raf.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
      <div ref={blobRef} className="ambient-blob" />
    </div>
  );
};

type PaymentsLayoutProps = {
  footer?: React.ReactNode;
  showLoadingIndicator?: boolean;
  collapseLeftNav?: boolean;
};

export const PaymentsLayout = ({
  children,
  footer,
  showLoadingIndicator,
  collapseLeftNav = false,
}: PropsWithChildren<PaymentsLayoutProps>) => {
  const deviceType = useDeviceSize();
  const [isLeftNavExpanded, setLeftNavExpanded] = useState<boolean>(
    !collapseLeftNav
  );
  const [showCollapseButton, setShowCollapseButton] = useState<boolean>(false);
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id;
  if (!workspaceId) {
    throw new Error('workspaceId not found');
  }
  const leftNavBackdropRef = useRef<HTMLDivElement>(null);
  const leftNavRef = useRef<HTMLDivElement>(null);

  const isMobileMenu = useMemo(
    () =>
      deviceType === DeviceSize.Phone ||
      deviceType === DeviceSize.Tablet ||
      deviceType === DeviceSize.WideTablet,
    [deviceType]
  );

  const closeLeftNavOnClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        leftNavBackdropRef.current &&
        event.target === leftNavBackdropRef.current &&
        isMobileMenu
      ) {
        setLeftNavExpanded(false);
      }
    },
    [setLeftNavExpanded, isMobileMenu]
  );

  useEffect(() => {
    document.addEventListener('mousedown', closeLeftNavOnClickOutside);
    return () => {
      document.removeEventListener('mousedown', closeLeftNavOnClickOutside);
    };
  }, [closeLeftNavOnClickOutside]);

  useEffect(() => {
    if (isMobileMenu) {
      setLeftNavExpanded(false);
    }
  }, [isMobileMenu]);

  useHotkeys('mod+/', () => {
    setLeftNavExpanded(!isLeftNavExpanded);
  });

  const twTransition = 'transition-all duration-100 menu-transition';

  return (
    <ToastProvider>
      <div
        className={clsx([
          'h-screen text-md flex flex-col overflow-x-hidden relative ',
        ])}
        data-testid="dashboard-layout"
      >
        <AmbientBlob />
        {/* width of the left nav is f21. to accomplish the animation,
        we slide the nav in from off screen to the left. We expand the
        nav bar to f25 when hidden so that the collapse / expand button
        remains on screen */}
        {isMobileMenu && (
          <div
            className={clsx([
              'fixed top-0 left-0 w-screen h-screen bg-main-500  ',
              twTransition,
              isLeftNavExpanded ? 'opacity-40 z-20' : 'opacity-0 -z-10',
            ])}
            ref={leftNavBackdropRef}
          />
        )}
        <div className={clsx(['flex flex-col h-full'])}>
          <div
            className={clsx([
              'h-full overflow-auto flex flex-row',
              twTransition,
            ])}
          >
            <header
              data-testid="main-nav-header"
              className={clsx([
                isLeftNavExpanded
                  ? 'left-0 w-f14 max-w-f14 justify-start'
                  : '-left-f14 laptop:-left-f14 w-f16 max-w-f16 justify-end',
                'absolute z-30 top-0 min-h-f4 max-h-f4 flex flex-row items-start pt-[10px] min-w-f14 ',
                twTransition,
              ])}
              onMouseEnter={() => setShowCollapseButton(true)}
            >
              <div className="flex flex-row items-start justify-between w-full pl-f1">
                <WorkspaceSwitcher
                  variant="payments"
                  disabled={!isLeftNavExpanded}
                />
                <TertiaryButton
                  className={clsx(
                    'h-f2 w-f2 min-w-f2 min-h-f2',
                    isMobileMenu && 'opacity-100',
                    !isLeftNavExpanded || showCollapseButton
                      ? 'opacity-100'
                      : 'opacity-0',
                    twTransition
                  )}
                  onClick={() => setLeftNavExpanded(!isLeftNavExpanded)}
                  data-testid="main-nav-collapse-button"
                >
                  <span
                    className={clsx(
                      'bg-canvas hover:bg-negative hover:text-main flex items-center',
                      'justify-center border-main-300 border-[0.5px] w-f2 h-f2 text-main-500 '
                    )}
                  >
                    {isLeftNavExpanded ? '«' : `☰`}
                  </span>
                </TertiaryButton>
              </div>
            </header>
            <div
              ref={leftNavRef}
              onMouseEnter={() => setShowCollapseButton(true)}
              onMouseLeave={() => setShowCollapseButton(false)}
              className={clsx([
                'fixed laptop:absolute',
                isLeftNavExpanded ? 'left-f0' : '-left-f14',
                'flex flex-col w-f14 min-w-f14 max-w-f14 overflow-y-auto scrollbar-hide',
                'border-r-[0.5px] border-main-300',
                'h-full z-20',
                'pl-f1 pr-f1 pt-f5 laptop:pt-f7',
                twTransition,
              ])}
            >
              <nav
                data-testid={
                  isLeftNavExpanded ? 'left-nav-open' : 'left-nav-closed'
                }
                className="pt-f2"
              >
                <PaymentsNav workspaceId={workspace.id} />
              </nav>
            </div>
            <main
              data-testid="layout-main"
              className={clsx([
                'w-full h-full scrollbar-hide',
                isLeftNavExpanded && 'laptop:grow laptop:pl-f14',
                twTransition,
              ])}
            >
              {children}
            </main>
          </div>
          {footer && (
            <footer data-testid="footer" className="mt-auto sticky bottom-0">
              {footer}
            </footer>
          )}
        </div>
        {showLoadingIndicator && (
          <div className="absolute bottom-f2 z-50 right-f2 bg-opacity-100 flex flex-col justify-end">
            <LoadingIndicator />
          </div>
        )}
      </div>
    </ToastProvider>
  );
};
